import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { 
  Package, 
  Truck, 
  Clock, 
  Box, 
  Send,
  CheckCircle,
  ArrowRight
} from "lucide-react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const QuotePage = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    company: "",
    service: "",
    volume: "",
    details: ""
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(`${API}/quote`, formData);
      setSubmitted(true);
      toast.success("Angebotsanfrage erfolgreich gesendet!");
      setFormData({
        name: "",
        email: "",
        phone: "",
        company: "",
        service: "",
        volume: "",
        details: ""
      });
    } catch (error) {
      console.error("Error submitting quote request:", error);
      toast.error("Fehler beim Senden. Bitte versuchen Sie es erneut.");
    } finally {
      setLoading(false);
    }
  };

  const services = [
    { value: "versand", label: "Verpackung & Versand", icon: Package },
    { value: "express", label: "Express & Luftfracht", icon: Clock },
    { value: "fulfillment", label: "Fulfillment & E-Commerce", icon: Package },
    { value: "verpackung", label: "Verpackungsservice", icon: Box },
    { value: "combination", label: "Kombination mehrerer Leistungen", icon: Package }
  ];

  const volumes = [
    { value: "small", label: "Bis 100 Sendungen/Monat" },
    { value: "medium", label: "100 - 500 Sendungen/Monat" },
    { value: "large", label: "500 - 2.000 Sendungen/Monat" },
    { value: "enterprise", label: "Über 2.000 Sendungen/Monat" },
    { value: "unsure", label: "Noch nicht sicher" }
  ];

  return (
    <div data-testid="quote-page">
      {/* Page Header */}
      <section className="page-header py-20 md:py-28 text-white">
        <div className="container mx-auto px-4 relative z-10">
          <span className="inline-block bg-orange-600 text-white text-sm uppercase tracking-widest font-bold px-4 py-2 mb-6">
            Angebot
          </span>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6">
            Angebot anfordern
          </h1>
          <p className="text-xl text-slate-300 max-w-2xl">
            Fordern Sie jetzt Ihr individuelles und unverbindliches Angebot an.
          </p>
        </div>
      </section>

      {/* Quote Form Section */}
      <section className="section-padding bg-white">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-3 gap-12">
            {/* Services Overview */}
            <div className="lg:col-span-1">
              <h2 className="text-2xl font-bold text-slate-900 mb-6">
                Unsere Leistungen
              </h2>
              <p className="text-slate-600 mb-8">
                Wählen Sie die gewünschte Leistung und wir erstellen Ihnen ein maßgeschneidertes Angebot.
              </p>
              
              <div className="space-y-4">
                {services.slice(0, 4).map((service, index) => {
                  const Icon = service.icon;
                  return (
                    <div 
                      key={service.value}
                      className={`p-4 border transition-all cursor-pointer ${
                        formData.service === service.value 
                          ? "border-orange-600 bg-orange-50" 
                          : "border-slate-200 hover:border-orange-300"
                      }`}
                      onClick={() => handleSelectChange("service", service.value)}
                      data-testid={`service-option-${index}`}
                    >
                      <div className="flex items-center gap-4">
                        <div className={`w-10 h-10 flex items-center justify-center ${
                          formData.service === service.value 
                            ? "bg-orange-600 text-white" 
                            : "bg-slate-100 text-slate-600"
                        }`}>
                          <Icon size={20} aria-hidden="true" />
                        </div>
                        <span className={`font-medium ${
                          formData.service === service.value 
                            ? "text-orange-600" 
                            : "text-slate-700"
                        }`}>
                          {service.label}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Benefits */}
              <div className="mt-8 p-6 bg-slate-50 border-l-4 border-orange-600">
                <h3 className="font-bold text-lg text-slate-900 mb-4">Ihre Vorteile</h3>
                <ul className="space-y-2 text-slate-600 text-sm">
                  <li className="flex items-start gap-2">
                    <CheckCircle className="text-orange-600 flex-shrink-0 mt-0.5" size={16} aria-hidden="true" />
                    Kostenlos & unverbindlich
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="text-orange-600 flex-shrink-0 mt-0.5" size={16} aria-hidden="true" />
                    Schnelle Rückmeldung
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="text-orange-600 flex-shrink-0 mt-0.5" size={16} aria-hidden="true" />
                    Individuelle Beratung
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="text-orange-600 flex-shrink-0 mt-0.5" size={16} aria-hidden="true" />
                    Transparente Preise
                  </li>
                </ul>
              </div>
            </div>

            {/* Quote Form */}
            <div className="lg:col-span-2">
              <div className="bg-slate-50 p-8 md:p-10">
                <h2 className="text-2xl font-bold text-slate-900 mb-2">
                  Ihre Anfrage
                </h2>
                <p className="text-slate-600 mb-8">
                  Füllen Sie das Formular aus und wir erstellen Ihnen ein individuelles Angebot.
                </p>

                {submitted ? (
                  <div className="text-center py-12" data-testid="quote-success">
                    <CheckCircle className="mx-auto text-green-500 mb-4" size={64} aria-hidden="true" />
                    <h3 className="text-2xl font-bold text-slate-900 mb-2">
                      Vielen Dank für Ihre Anfrage!
                    </h3>
                    <p className="text-slate-600 mb-2">
                      Wir haben Ihre Angebotsanfrage erhalten und werden uns schnellstmöglich bei Ihnen melden.
                    </p>
                    <p className="text-slate-500 text-sm mb-6">
                      In der Regel erhalten Sie innerhalb von 24 Stunden eine Rückmeldung.
                    </p>
                    <Button 
                      className="bg-orange-600 hover:bg-orange-700 text-white"
                      onClick={() => setSubmitted(false)}
                    >
                      Weitere Anfrage stellen
                    </Button>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-6" data-testid="quote-form">
                    {/* Contact Info */}
                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <Label htmlFor="name" className="text-slate-700 font-medium">
                          Ansprechpartner *
                        </Label>
                        <Input
                          id="name"
                          name="name"
                          type="text"
                          required
                          value={formData.name}
                          onChange={handleChange}
                          className="mt-2 border-slate-300 focus:border-orange-500 bg-white"
                          placeholder="Ihr Name"
                          data-testid="quote-name-input"
                        />
                      </div>
                      <div>
                        <Label htmlFor="company" className="text-slate-700 font-medium">
                          Unternehmen *
                        </Label>
                        <Input
                          id="company"
                          name="company"
                          type="text"
                          required
                          value={formData.company}
                          onChange={handleChange}
                          className="mt-2 border-slate-300 focus:border-orange-500 bg-white"
                          placeholder="Ihr Unternehmen"
                          data-testid="quote-company-input"
                        />
                      </div>
                    </div>

                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <Label htmlFor="email" className="text-slate-700 font-medium">
                          E-Mail *
                        </Label>
                        <Input
                          id="email"
                          name="email"
                          type="email"
                          required
                          value={formData.email}
                          onChange={handleChange}
                          className="mt-2 border-slate-300 focus:border-orange-500 bg-white"
                          placeholder="ihre@email.de"
                          data-testid="quote-email-input"
                        />
                      </div>
                      <div>
                        <Label htmlFor="phone" className="text-slate-700 font-medium">
                          Telefon *
                        </Label>
                        <Input
                          id="phone"
                          name="phone"
                          type="tel"
                          required
                          value={formData.phone}
                          onChange={handleChange}
                          className="mt-2 border-slate-300 focus:border-orange-500 bg-white"
                          placeholder="Ihre Telefonnummer"
                          data-testid="quote-phone-input"
                        />
                      </div>
                    </div>

                    {/* Service Selection */}
                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <Label htmlFor="service" className="text-slate-700 font-medium">
                          Gewünschte Leistung *
                        </Label>
                        <Select 
                          value={formData.service} 
                          onValueChange={(value) => handleSelectChange("service", value)}
                          required
                        >
                          <SelectTrigger 
                            className="mt-2 border-slate-300 focus:border-orange-500 bg-white"
                            data-testid="quote-service-select"
                          >
                            <SelectValue placeholder="Leistung auswählen" />
                          </SelectTrigger>
                          <SelectContent>
                            {services.map((service) => (
                              <SelectItem key={service.value} value={service.value}>
                                {service.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label htmlFor="volume" className="text-slate-700 font-medium">
                          Erwartetes Volumen
                        </Label>
                        <select
                          id="volume"
                          name="volume"
                          value={formData.volume}
                          onChange={handleChange}
                          className="mt-2 w-full h-10 px-3 border border-slate-300 bg-white focus:border-orange-500 focus:outline-none focus:ring-2 focus:ring-orange-500/20"
                          data-testid="quote-volume-select"
                        >
                          <option value="">Volumen auswählen</option>
                          {volumes.map((volume) => (
                            <option key={volume.value} value={volume.value}>
                              {volume.label}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>

                    {/* Details */}
                    <div>
                      <Label htmlFor="details" className="text-slate-700 font-medium">
                        Beschreibung Ihrer Anforderungen *
                      </Label>
                      <Textarea
                        id="details"
                        name="details"
                        required
                        value={formData.details}
                        onChange={handleChange}
                        className="mt-2 border-slate-300 focus:border-orange-500 bg-white min-h-[150px]"
                        placeholder="Beschreiben Sie Ihre Anforderungen, z.B. Art der Waren, Liefergebiete, besondere Wünsche..."
                        data-testid="quote-details-input"
                      />
                    </div>

                    <Button 
                      type="submit" 
                      disabled={loading}
                      className="bg-orange-600 hover:bg-orange-700 text-white uppercase tracking-wider font-bold px-8 py-6 w-full md:w-auto"
                      data-testid="quote-submit-button"
                    >
                      {loading ? (
                        "Wird gesendet..."
                      ) : (
                        <>
                          Angebot anfordern
                          <ArrowRight className="ml-2" size={18} aria-hidden="true" />
                        </>
                      )}
                    </Button>
                  </form>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Section */}
      <section className="dark-section text-white py-16">
        <div className="container mx-auto px-4 relative z-10">
          <div className="text-center max-w-2xl mx-auto">
            <h2 className="text-2xl md:text-3xl font-bold mb-4">
              Vertrauen Sie auf unsere Expertise
            </h2>
            <p className="text-slate-300">
              Mit langjähriger Erfahrung und modernster Technik sind wir Ihr zuverlässiger Partner 
              für alle Logistikanforderungen.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default QuotePage;
